import { captureException } from "@sentry/nextjs";
import {
  AnyPlanAliasType,
  IBasePlan,
  IFilterPlansProps,
  IGetPlanAliasProps,
  IPlan,
  ParentPlanType,
  PlanAliasType,
} from "./types";
import { PARENT_PLAN_TYPES, PLAN_ALIAS_BUSINESS } from "./constants";
import {
  isDevPlan,
  isProPlan,
  isSelfHostedPlan,
  isStarterPlan,
  isYearlyPlan,
} from "@common/utils/plan";
import { pluralize } from "@common/utils/string";
import { PLAN_EXTRAS } from "@common/utils/plan/constants";

// TEMP
// Prices from the API are formatted inconsistently
// We need to convert them to float
export function convertToNumber(value: string | number): number {
  if (typeof value === "string") {
    return parseFloat(value.replace("$", "").replace(",", ""));
  }

  return value;
}

export function formatPlansData(data: IBasePlan[]): IPlan[] {
  return (
    data
      // only available plans
      ?.filter(({ can_purchase }) => can_purchase)
      .filter(({ alias }) => String(alias) !== PLAN_ALIAS_BUSINESS)
      // populate extra content
      // not present (yet) in the API response
      .map((planObj) => {
        const { alias } = planObj;
        if (PLAN_EXTRAS[alias]) {
          return { ...planObj, ...PLAN_EXTRAS[alias] };
        }

        return planObj as IPlan;
      })
      // convert price to number
      .map((obj) => ({
        ...obj,
        per_user_price: convertToNumber(obj.per_user_price),
        price: convertToNumber(obj.price),
      }))
  );
}

export function filterPlans(
  plans: IPlan[],
  {
    pro: filterPro,
    yearly: filterYearly,
    selfHosted: filterSelfHosted,
    dev: filterDev,
  }: IFilterPlansProps = {},
): IPlan[] {
  return (
    plans
      .filter(({ alias }) => {
        if (filterPro === undefined) {
          return true;
        }

        return filterPro ? isProPlan(alias) : !isProPlan(alias);
      })
      .filter(({ alias }) => {
        if (filterYearly === undefined) {
          return true;
        }

        return filterYearly ? isYearlyPlan(alias) : !isYearlyPlan(alias);
      })
      .filter(({ alias }) => {
        if (filterSelfHosted === undefined) {
          return true;
        }

        return filterSelfHosted
          ? isSelfHostedPlan(alias)
          : !isSelfHostedPlan(alias);
      })
      .filter(({ alias }) => {
        if (filterDev === undefined) {
          return true;
        }

        return filterDev ? isDevPlan(alias) : !isDevPlan(alias);
      })
      // LEGACY
      // plans with alias containing -with-dwh
      // are not available anymore
      // as storage is now an add-on
      .filter(({ alias }) => !alias.includes("-with-dwh"))

      // Sorting plans
      .sort((plan1, plan2) => {
        // Starter
        if (isStarterPlan(plan1.alias) && !isStarterPlan(plan2.alias)) {
          return -1;
        }
        // Yearly
        else if (isYearlyPlan(plan1.alias) && !isYearlyPlan(plan2.alias)) {
          return 1;
        }

        return 1;
      })
  );
}

export function isMonthlyPlan({
  billing_period_months,
}: Pick<IBasePlan, "billing_period_months">): boolean {
  return !!(billing_period_months === 1);
}

export function isAnnualPlan({
  billing_period_months,
}: Pick<IBasePlan, "billing_period_months">): boolean {
  return !!(billing_period_months === 12);
}

export function getBillingFrequencyCopy({
  billing_period_months,
}: Pick<IBasePlan, "billing_period_months">): string {
  // monthy
  if (billing_period_months === 1) {
    return "Monthly Billing";
  }
  // yearly
  else if (billing_period_months === 12) {
    return "Annual Billing";
  }

  captureException(
    new Error(
      `getBillingFrequencyCopy error: unknown billing frequency of "${billing_period_months}"`,
    ),
  );

  return `Billing every ${billing_period_months} ${pluralize("month", billing_period_months)}`;
}

export function getParentPlanAlias(alias: AnyPlanAliasType): ParentPlanType {
  const parentPlanAlias = alias
    .replace("-cloud", "")
    .replace("-self-hosted", "")
    .replace("-yearly", "")
    .replace("-dev", "")

    // LEGACY
    // starter-v2 => starter
    .replace("starter-v2", "starter")
    // startup => starter
    .replace("startup", "starter")
    // storage
    .replace("-with-dwh", "") as ParentPlanType;

  if (!PARENT_PLAN_TYPES.includes(parentPlanAlias)) {
    throw new Error(`getParentPlanAlias, invalid alias: ${alias}`);
  }

  return parentPlanAlias;
}

export function buildPlanAlias({
  basePlanAlias,
  isSelfHosted,
  isYearly,
  isDev,
}: IGetPlanAliasProps): PlanAliasType {
  // starter OR pro
  const parentPlanAlias = getParentPlanAlias(basePlanAlias);

  // plan options
  const deploymentStr = isSelfHosted ? "-self-hosted" : "-cloud";
  const billingStr = isYearly ? "-yearly" : "";
  const devStr = isDev ? "-dev" : "";
  const planSuffix = isStarterPlan(basePlanAlias)
    ? `${billingStr}`
    : `${deploymentStr}${devStr}${billingStr}`;

  return `${parentPlanAlias}${planSuffix}` as PlanAliasType;
}
